import React, { useEffect, useState } from "react";
import {
  Box, Grid, Paper, Typography, TextField,
  Button, Dialog, DialogTitle, DialogContent, DialogActions, InputAdornment
} from "@mui/material";
import SearchIcon from '@mui/icons-material/Search';
import { useDispatch, useSelector } from "react-redux";
import {
  getStockManagementAction,
  editStockManagementAction,
  clearEditStockManagementAction
} from "../../store/actions";
import toast from "react-hot-toast";

const Stockmanagement = () => {
  const dispatch = useDispatch();
  const [searchTerm, setSearchTerm] = useState('');
  const [receivedQtyMap, setReceivedQtyMap] = useState({});
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [currentItem, setCurrentItem] = useState(null);

  const { successData } = useSelector((state) => state.getStockReducer);
  const { successData: editSuccessData } = useSelector((state) => state.editStockReducer);

  useEffect(() => {
    dispatch(getStockManagementAction());
  }, [dispatch]);

  useEffect(() => {
    if (editSuccessData) {
      toast.success(editSuccessData.message || "Stock updated successfully");
      dispatch(getStockManagementAction());
    }
    return () => {
      dispatch(clearEditStockManagementAction());
    };
  }, [editSuccessData, dispatch]);

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
  };

  const handleQtyChange = (id, value) => {
    setReceivedQtyMap(prev => ({ ...prev, [id]: value }));
  };

  const handleUpdateClick = (item) => {
    setCurrentItem(item);
    setConfirmDialogOpen(true);
  };

  const handleConfirmUpdate = () => {
    const receivedQty = Number(receivedQtyMap[currentItem.id]);
    if (!receivedQty || receivedQty <= 0) {
      toast.error("Enter valid quantity");
      return;
    }
    const updatedQty = currentItem.stock_qty + receivedQty;
    dispatch(editStockManagementAction({
      endPoint: `stock-manager/edit-raw-material/${currentItem.id}`,
      stock_qty: updatedQty
    }));
    setConfirmDialogOpen(false);
    setCurrentItem(null);
    setReceivedQtyMap(prev => ({ ...prev, [currentItem.id]: '' }));
  };

  const filteredMaterials = successData?.data
    ?.filter(mat => mat.name.toLowerCase().includes(searchTerm.toLowerCase()))
    .sort((a, b) => a.name.localeCompare(b.name)) || [];

  return (
    <Box
      sx={{
        marginTop: { xs: "25%", sm: "15%", md: "10%" },
        px: { xs: 1, sm: 4, md: 0 },
        width: { xs: '95%', sm: '100%', md: '79%' },
        ml: { xs: 0, sm: 0, md: '21%' }
      }}
    >
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <h1 style={{ fontWeight: 600, fontSize: "2rem", fontFamily: "Poppins" }}>
            Stock Management
          </h1>
          <Box
            sx={{
              display: 'flex',
              justifyContent: { xs: 'center', sm: 'center', md: 'flex-end' },
              width: '100%',
              mb: 2,
            }}
          >
            <TextField
              sx={{
                width: {
                  xs: '100%',
                  sm: '100%',
                  md: '29%',
                },
                mr: { xs: 0, sm: 0, md: 2 },
              }}
              variant="outlined"
              placeholder="Search materials..."
              value={searchTerm}
              onChange={handleSearchChange}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
            />
          </Box>
        </Grid>

        {filteredMaterials.length === 0 ? (
          <Grid item xs={12}>
            <Paper sx={{ p: 2, textAlign: 'center' }}>No materials found.</Paper>
          </Grid>
        ) : (
          filteredMaterials.map((item) => (
            <Grid
              item
              xs={12}
              sm={6}
              md={4}
              key={item.id}
              sx={{
                display: 'flex',
                justifyContent: {
                  xs: 'center',
                  sm: 'flex-start',
                  md: 'flex-start',
                },
              }}
            >
              <Paper
                elevation={3}
                sx={{
                  p: 2,
                  width: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'space-between',
                  borderRadius: 2,
                  boxShadow: '0 1px 6px rgba(0, 0, 0, 0.1)',
                }}
              >
                <Box>
                  <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                    {item.name}
                  </Typography>
                  <Typography variant="body2" sx={{ mt: 0.5 }}>
                    <strong>Type:</strong> {item.qty_type}
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      mt: 0.5,
                      fontWeight: 'bold',
                      color: item.stock_qty >= item.reorder_level ? 'green' : 'red',
                    }}
                  >
                    <strong>Stock:</strong> {item.stock_qty}
                  </Typography>
                </Box>

                <Box sx={{ mt: 2, display: 'flex', gap: 1 }}>
                  <TextField
                    variant="outlined"
                    size="small"
                    type="number"
                    placeholder="Enter Recieved Stock Qty"
                    value={receivedQtyMap[item.id] || ''}
                    onChange={(e) => handleQtyChange(item.id, e.target.value)}
                    sx={{ flex: 1 }}
                  />
                  <Button
                    variant="contained"
                    size="small"
                    color="primary"
                    onClick={() => handleUpdateClick(item)}
                    sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 500 }}
                  >
                    Update
                  </Button>
                </Box>
              </Paper>
            </Grid>
          ))
        )}
      </Grid>

      <Dialog open={confirmDialogOpen} onClose={() => setConfirmDialogOpen(false)}>
        <DialogTitle>Confirm Update</DialogTitle>
        <DialogContent>
          <Typography>Are you sure you want to update the stock?</Typography>
          {currentItem && (
            <Typography sx={{ mt: 1 }}>
              <strong>{currentItem.name}</strong> – Add {receivedQtyMap[currentItem.id]} {currentItem.qty_type}
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDialogOpen(false)} color="secondary">
            Cancel
          </Button>
          <Button onClick={handleConfirmUpdate} color="primary">
            Confirm
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Stockmanagement;
